"use client";

import { useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";

// A "back" that never takes the visitor out of the site. From chawk-bazar, ciseco and jument `useSafeBack`
// (same tracker; they differed in the fallback url and the history key).
//
// Each history entry carries its number in `history.state[state_key]`; 0 means the first page of the site in this
// tab, so the browser's back would leave the site. Counting seen paths was not enough: `router.replace` (e.g. the
// redirect after login) changes the path without a new history entry.

export const DEFAULT_HISTORY_STATE_KEY = "__history_idx";

// trackers live on window (one per state key), so HMR or a second copy of this module installs once
const TRACKERS_KEY = "__core_history_trackers";

// site-wide defaults, set once by `setup_safe_back`
const site_options = { state_key: DEFAULT_HISTORY_STATE_KEY, fallback_url: "/", get_fallback_url: null };

const read_idx = (state, key) => (typeof state?.[key] === "number" ? state[key] : null);

const with_idx = (state, key, idx) => {
	if (state == null) return { [key]: idx };
	if (typeof state === "object" && !Array.isArray(state)) return { ...state, [key]: idx };
	return state;
};

// a new entry made by a full page load from another page of this site
const came_from_site = () => {
	if (window.history.length <= 1) return false;
	try {
		const referrer = new URL(document.referrer);
		return referrer.origin === window.location.origin && referrer.href !== window.location.href;
	} catch {
		return false;
	}
};

const get_tracker = (state_key) => (typeof window === "undefined" ? null : window[TRACKERS_KEY]?.[state_key] || null);

// Patches pushState/replaceState to number the entries. Idempotent; does nothing on the server.
export const install_history_tracking = ({ state_key = site_options.state_key } = {}) => {
	if (typeof window === "undefined") return null;
	if (!window[TRACKERS_KEY]) window[TRACKERS_KEY] = {};
	const trackers = window[TRACKERS_KEY];
	if (trackers[state_key]) return trackers[state_key];

	const { history } = window;
	const tracker = { idx: read_idx(history.state, state_key) };
	trackers[state_key] = tracker;

	const original_push = history.pushState;
	const original_replace = history.replaceState;

	if (tracker.idx === null) {
		tracker.idx = came_from_site() ? 1 : 0;
		original_replace.call(history, with_idx(history.state, state_key, tracker.idx), "");
	}

	history.pushState = function (state, ...rest) {
		tracker.idx += 1;
		return original_push.call(this, with_idx(state, state_key, tracker.idx), ...rest);
	};

	history.replaceState = function (state, ...rest) {
		return original_replace.call(this, with_idx(state, state_key, tracker.idx), ...rest);
	};

	window.addEventListener("popstate", (event) => {
		const idx = read_idx(event.state, state_key);
		if (idx !== null) tracker.idx = idx;
	});

	return tracker;
};

// Site-wide defaults + early install. Call once at the top level of a client module that loads on every page
// (before the Next router hydrates), so the first navigations are counted too.
export const setup_safe_back = ({ state_key, fallback_url, get_fallback_url } = {}) => {
	if (state_key) site_options.state_key = state_key;
	if (fallback_url) site_options.fallback_url = fallback_url;
	if (typeof get_fallback_url === "function") site_options.get_fallback_url = get_fallback_url;
	return install_history_tracking({ state_key: site_options.state_key });
};

// true when the previous history entry is a page of this site
export const has_in_site_history = ({ state_key = site_options.state_key } = {}) =>
	(get_tracker(state_key)?.idx ?? 0) > 0;

// Where "back" goes without site history: `get_fallback_url(pathname)` if given (and it returns a url), else `fallback_url`.
export const resolve_back_fallback_url = (pathname = "", options = {}) => {
	const get_fallback_url = options.get_fallback_url || site_options.get_fallback_url;
	const from_resolver = typeof get_fallback_url === "function" ? get_fallback_url(pathname) : null;
	return from_resolver || options.fallback_url || site_options.fallback_url || "/";
};

// Returns a callback: `router.back()` when the previous entry is this site, otherwise `router.replace(fallback)`
// (replace: the current page does not stay as the entry "before" home).
// options (override `setup_safe_back` for this call): { fallback_url, get_fallback_url(pathname), state_key }.
// Pass a stable `get_fallback_url` (module level), or the callback changes every render.
export default function useSafeBack(options = {}) {
	const { fallback_url, get_fallback_url, state_key } = options;
	const key = state_key || site_options.state_key;

	// safety net when `setup_safe_back` was not called; idempotent
	install_history_tracking({ state_key: key });

	const router = useRouter();
	const pathname = usePathname() || "";

	return useCallback(() => {
		if (has_in_site_history({ state_key: key })) router.back();
		else router.replace(resolve_back_fallback_url(pathname, { fallback_url, get_fallback_url }));
	}, [router, pathname, key, fallback_url, get_fallback_url]);
}
